import {Async, isLoaded} from 'Core/models/Async';
import {GAI7RidersPropertyPath} from 'Quoting/components/QuoteForm/Plan/PlanTemplates/ACC/benefitPropertyMap';
import {
  AccelDeathBenefitType,
  GPLU_COVERAGE_CODE_TO_COVERAGE_TYPE_MAP,
  GPLUCoverageCode,
} from 'Quoting/components/QuoteForm/Plan/PlanTemplates/GWL/gpluConstants';
import {BROKER_PORTAL_SELF_SERVE_UW_OFFER, OPENL_DEFAULTS_CONFIG_DATE} from 'Quoting/constants';
import {RatePremiumDetails} from 'Quoting/models/DTOs/RatePremium';
import {A360ProductCd, A360ProductModel} from 'Quoting/models/Quoting';
import {getProductCdFromModel} from 'Quoting/utils/helpers';
import {isA360HealthProduct, isDefined, parsePath} from 'Quoting/utils/utilities';
import {List as ImmutableList, Map as ImmutableMap} from 'immutable';
import {
  AttributeBehavior,
  AttributeValue,
  GpluAgeBandsAndSmokingFlags,
  PredefinedHierarchicalContractsRequestPayload,
  RetrievePlanConfigurationRequestPayload,
  RetrievePlansRequestData,
  RetrievePlansWithCoverageRequestPayload,
} from './types';
import {chain, map} from 'fp-ts/lib/Array';
import {pipe} from 'fp-ts/lib/pipeable';

export const getRetrievePlansWithCoverageRequestPayload = (requestData: RetrievePlansRequestData) => {
  const {
    productModel,
    sicCode,
    numberOfEligibleLives,
    riskStateCode,
    termEffectiveDate,
    rateEffectiveDate,
    rateRequestDate,
  } = requestData;

  const productCd = getProductCdFromModel(productModel);

  const attributes: RetrievePlansWithCoverageRequestPayload['attributes'] = [
    {
      _type: 'DataItem',
      id: '/',
      ...getEntityAndAttribute(`${productCd}MasterPolicySummary`, 'sicCode', 'String', sicCode),
    },
  ];

  if (productModel === A360ProductModel.GPLUMaster) {
    return {
      attributes,
      dimensions: [
        {
          id: '/',
          ...getEntityAndAttribute('GPLUMasterBenefitPolicyPackage', 'packageCd', 'String', 'WL'),
        },
        {
          id: '/',
          ...getEntityAndAttribute('GPLUMasterPolicySummary', 'riskStateCd', 'String', riskStateCode),
        },
        {
          id: '/',
          ...getEntityAndAttribute(
            'GPLUMasterPolicyDetail',
            'quoteUWOffer',
            'String',
            BROKER_PORTAL_SELF_SERVE_UW_OFFER
          ),
        },
      ],
    };
  } 

  const requestPayload: RetrievePlansWithCoverageRequestPayload = {
    attributes: attributes,
    dimensions: [
      {
        id: '/',
        ...getEntityAndAttribute(
          `${productCd}EligibleLivesAndParticipation`,
          'numberOfEligibleLives',
          'Integer',
          numberOfEligibleLives
        ),
      },
      {
        id: '/',
        ...getEntityAndAttribute(`${productCd}MasterPolicySummary`, 'riskStateCd', 'String', riskStateCode),
      },
    ],
    rateEffectiveDate: rateEffectiveDate,
    rateRequestDate: rateRequestDate,
    _type: 'BehaviorRequest',
  };

  if (productCd === A360ProductCd.HI) {
    requestPayload.attributes = requestPayload.attributes.concat({
      _type: 'DataItem',
      id: '/termDetails',
      ...getEntityAndAttribute('HITermDetails', 'termEffectiveDate', 'Datetime', termEffectiveDate),
    });

    requestPayload.dimensions = requestPayload.dimensions.concat({
      id: '/',
      ...getEntityAndAttribute('HIPolicyDetail', 'brokerUWOffer', 'String', BROKER_PORTAL_SELF_SERVE_UW_OFFER),
    });
  }
  return requestPayload;
};

export const getRetrieveAttributeValuesPayload = (
  requestData: RetrievePlanConfigurationRequestPayload,
  coverageCd?: string
) => {
  const {
    planCd,
    productModel,
    situsStateCd,
    numberOfEligibleLives,
    rateEffectiveDate,
    rateRequestDate,
    packageCd,
  } = requestData;

  const productCd = getProductCdFromModel(productModel) as A360ProductCd;

  if (productCd === A360ProductCd.GPLU) {
    let dimensions = [
      {
        id: '/',
        ...getEntityAndAttribute('GPLUMasterPolicySummary', 'riskStateCd', 'String', situsStateCd),
      },
      {
        id: '/',
        ...getEntityAndAttribute('GPLUMasterBenefitPolicyPackage', 'packageCd', 'String', packageCd),
      },
      {
        id: '/',
        ...getEntityAndAttribute('GPLUMasterPolicyDetail', 'quoteUWOffer', 'String', BROKER_PORTAL_SELF_SERVE_UW_OFFER),
      },
    ];

    if (planCd) {
      dimensions = dimensions.concat([
        {
          id: '/plans/0',
          ...getEntityAndAttribute('GPLUMasterBenefitPlan', 'planCd', 'String', planCd),
        },
        {
          id: '/plans/0/coverageDefs/0',
          ...getEntityAndAttribute(
            getCoverageTypeFromCode(productCd, coverageCd ?? 'WholeLife'),
            'code',
            'String',
            coverageCd ?? 'WholeLife'
          ),
        },
      ]);
    }
    if (coverageCd === GPLUCoverageCode.ACCEL_DEATH_BENEFIT_LIFE) {
      dimensions = dimensions.concat([
        {
          id: '/plans/0/coverageDefs/0',
          ...getEntityAndAttribute(
            'AccelDeathBenefitCoverage',
            'accelDeathBenefitTypeCd',
            'String',
            AccelDeathBenefitType.LTC_Restoration_Extension
          ),
        },
      ]);
    }
    return {
      attributes: [],
      dimensions,
      scoped: false,
      rateEffectiveDate: '2026-02-01T00:00:00.000Z',
      rateRequestDate: '2026-02-09T00:00:00.000Z',
    };
  }

  const payload: Record<string, any> = {
    dimensions: [
      {
        id: '/',
        ...getEntityAndAttribute(`${productCd}MasterPolicySummary`, 'riskStateCd', 'String', situsStateCd),
      },
      {
        id: '/plans/0',
        ...getEntityAndAttribute(`${productCd}BenefitPlan`, 'planCd', 'String', planCd),
      },
      {
        id: '/plans/0/coverageDefs/0/',
        ...getEntityAndAttribute(`${productCd}CoverageDefinition`, 'code', 'String', coverageCd),
      },
      {
        id: '/',
        ...getEntityAndAttribute(
          `${productCd}PolicyPackage`,
          'packageCd',
          'String',
          getPackageCd(productCd, planCd ?? '')
        ),
      },
      {
        id: '/',
        ...getEntityAndAttribute(
          `${productCd}EligibleLivesAndParticipation`,
          'numberOfEligibleLives',
          'Integer',
          numberOfEligibleLives
        ),
      },
      {
        id: '/',
        ...getEntityAndAttribute(
          `${productCd}PolicyDetail`,
          productCd === 'HI' ? 'brokerUWOffer' : 'quoteUWOffer',
          'String',
          BROKER_PORTAL_SELF_SERVE_UW_OFFER
        ),
      },
    ],
    scoped: true,
    attributes: [],
  };

  if ([A360ProductModel.HIMaster, A360ProductModel.ACCMaster].includes(productModel)) {
    payload['rateEffectiveDate'] = rateEffectiveDate;
    payload['rateRequestDate'] = rateRequestDate;
  }
  return payload;
};

const getPackageCd = (productCd: A360ProductCd, planCd: string) => {
  switch (planCd) {
    case 'GAI7':
      return `${productCd}Package`;
    case 'GVA6':
      return `${productCd}PackageGVA6`;
    case 'GCI5':
      return `${productCd}Package`;
    case 'GIM2':
      return `${productCd}Package`;
    case 'GHI3':
      return `${productCd}PackageGHI3`;
    case 'GVCI4':
      return `${productCd}PackageGVCI4`;
    case 'Plan1': // GPLU/GWL Plan
      return 'WL';
  }
};

export const getRetrievePlanDefaultValuesPayload = (
  productModel: A360ProductModel,
  planCd: string,
  riskStateCd: string,
  numberOfEligibleLives: number,
  behaviorType: AttributeBehavior,
  filterByMaturityAge?: boolean, // required for GWL
  coverageCd?: string
) => {
  const productCd = getProductCdFromModel(productModel) as A360ProductCd;

  if (productCd === A360ProductCd.GPLU) {
    return getGPLUDefaultsRequestPayload(
      planCd,
      riskStateCd,
      numberOfEligibleLives,
      productCd,
      coverageCd,
      filterByMaturityAge
    );
  }

  return {
    _type: 'BehaviorRequest',
    effectiveDate: OPENL_DEFAULTS_CONFIG_DATE,
    transactionDate: OPENL_DEFAULTS_CONFIG_DATE,
    scoped: true,
    behaviorTypes: [behaviorType],
    dimensions: [
      {
        _type: 'DataItem',
        id: '/benefitPackagingDetail',
        ...getEntityAndAttribute(`${productCd}PolicyPackage`, 'packageCd', 'String', getPackageCd(productCd, planCd)),
      },
    ],
    attributes: [
      {
        _type: 'DataItem',
        id: '/benefitPackagingDetail/plans/0',
        ...getEntityAndAttribute(`${productCd}BenefitPlan`, 'planCd', 'String', planCd),
      },
      {
        _type: 'DataItem',
        id: '/benefitPackagingDetail',
        ...getEntityAndAttribute(`${productCd}PolicyPackage`, 'packageCd', 'String', getPackageCd(productCd, planCd)),
      },
      {
        _type: 'DataItem',
        id: '/',
        ...getEntityAndAttribute(`${productCd}MasterPolicySummary`, 'riskStateCd', 'String', riskStateCd),
      },
      {
        _type: 'DataItem',
        id: '/',
        ...getEntityAndAttribute(
          `${productCd}PolicyDetail`,
          productCd === 'HI' ? 'brokerUWOffer' : 'quoteUWOffer',
          'String',
          BROKER_PORTAL_SELF_SERVE_UW_OFFER
        ),
      },
      {
        _type: 'DataItem',
        id: '/',
        ...getEntityAndAttribute(
          `${productCd}EligibleLivesAndParticipation`,
          'numberOfEligibleLives',
          'Integer',
          numberOfEligibleLives
        ),
      },
      {
        _type: 'DataItem',
        id: '/_context',
        ...getEntityAndAttribute('_Context', 'action', 'String', 'Default apply'),
      },
    ],
  };
};

export const getPlanConfigurationRequestPayload = (
  quote: Async<ImmutableMap<string, any>> | undefined,
  selectedPlanCd?: string,
  coverageCodes?: string[]
): RetrievePlanConfigurationRequestPayload => {
  if (!quote || !isLoaded(quote)) {
    throw new Error('quote data is not loaded');
  }

  const quotePlans = quote.Data.getIn(parsePath('benefitPackagingDetail.plans'));

  const payload: RetrievePlanConfigurationRequestPayload = {
    productModel: quote.Data.get('_modelName') as A360ProductModel,
    numberOfEligibleLives:
      quote.Data?.get('_modelName') === A360ProductModel.GPLUMaster
        ? (quote.Data?.getIn(parsePath('eligibilityInfo.numberOfEligibleLives')) as number)
        : (quote.Data.getIn(parsePath('participationDetails.numberOfEligibleLives')) as number),
    packageCd: quote.Data.getIn(parsePath('benefitPackagingDetail.packageCd')) as string,
    rateEffectiveDate:
      quote.Data?.get('_modelName') === A360ProductModel.GPLUMaster ? undefined : quote.Data.get('rateEffectiveDate'),
    rateRequestDate:
      quote.Data?.get('_modelName') === A360ProductModel.GPLUMaster ? undefined : quote.Data.get('rateRequestDate'),
    situsStateCd: quote.Data.get('riskStateCd'),
  };

  if (selectedPlanCd) {
    payload.planCd = selectedPlanCd;
    payload.coverageCodes = coverageCodes;
  }

  if (!selectedPlanCd && ImmutableList.isList(quotePlans) && quotePlans?.size > 0) {
    payload.planCd = (quotePlans?.get(0) as ImmutableMap<string, any>)?.get('planCd');
  }
  return payload;
};

export const getUpdatedQuoteObject = (quote: Record<string, any>) => {
  if (quote._modelName === A360ProductModel.ACCMaster && quote?.benefitPackagingDetail?.plans) {
    quote.benefitPackagingDetail.plans.forEach((plan: Record<string, any>) => {
      plan?.coverageDefs.forEach((coverage: Record<string, any>) => {
        GAI7RidersPropertyPath?.forEach(({isAppliedPath, details}) => {
          if (!coverage.benefitStructure?.[isAppliedPath]) {
            delete coverage.benefitStructure?.[details];
          }
        });
      });
    });
    return quote;
  }
  return quote;
};

const requiredCIRecurrenceFields = [
  'coronaryArteryDiseasePct',
  'coveredConditionsTypeCd',
  'heartAttackPct',
  'kidneyRenalFailurePct',
  'majorOrganFailurePct',
  'strokePct',
  '_type',
  '_key',
];

export const prepareOfferAPIPayload = (quote: Record<string, any>) => {
  if (quote._modelName === A360ProductModel.CIMaster) {
    quote.benefitPackagingDetail.plans.forEach(
      plan =>
        (plan.coverageDefs[0].benefitSchedule.coveredConditionsDetails[1] = requiredCIRecurrenceFields.reduce(
          (acc: Record<string, any>, current) => {
            acc[current] = plan.coverageDefs[0].benefitSchedule.coveredConditionsDetails[1][current];
            return acc;
          },
          {}
        ))
    );
    return quote;
  }
  return quote;
};

export const getGPLUDefaultsRequestPayload = (
  planCd: string,
  riskStateCd: string,
  numberOfEligibleLives: number,
  productCd: A360ProductCd,
  coverageCd?: string,
  filterForMaturityAge: boolean = false
) => {
  const dimensions = filterForMaturityAge
    ? []
    : [
        {
          _type: 'DataItem',
          id: '/',
          ...getEntityAndAttribute(
            'GPLUMasterBenefitPolicyPackage',
            'packageCd',
            'String',
            getPackageCd(A360ProductCd.GPLU, planCd)
          ),
        },
        {
          _type: 'DataItem',
          id: '/',
          ...getEntityAndAttribute('GPLUMasterPolicySummary', 'riskStateCd', 'String', riskStateCd),
        },
        {
          _type: 'DataItem',
          id: '/',
          ...getEntityAndAttribute(
            'GPLUMasterPolicyDetail',
            'quoteUWOffer',
            'String',
            BROKER_PORTAL_SELF_SERVE_UW_OFFER
          ),
        },
        {
          _type: 'DataItem',
          id: '',
          ...getEntityAndAttribute(
            'GPLUMasterEligibleLivesAndParticipation',
            'numberOfEligibleLives',
            'Integer',
            numberOfEligibleLives
          ),
        },
        {
          _type: 'DataItem',
          id: '/_context',
          ...getEntityAndAttribute('_Context', 'action', 'String', 'Default apply'),
        },
        {
          _type: 'DataItem',
          id: '/',
          ...getEntityAndAttribute(
            getCoverageTypeFromCode(productCd, coverageCd ?? 'WholeLife'),
            'code',
            'String',
            coverageCd ?? 'WholeLife'
          ),
        },
      ];

  if (planCd) {
    dimensions.push({
      _type: 'DataItem',
      id: '/benefitPackagingDetail/plans/0',
      ...getEntityAndAttribute('GPLUMasterBenefitPlan', 'planCd', 'String', planCd),
    });
  }

  return {
    _type: 'BehaviorRequest',
    effectiveDate: OPENL_DEFAULTS_CONFIG_DATE,
    transactionDate: OPENL_DEFAULTS_CONFIG_DATE,
    scoped: true,
    behaviorTypes: ['AttributeDefaultBehavior'],
    validationTypes: [],
    filters: filterForMaturityAge
      ? [
          {...getFilterForOpenLAPI('GPLUMasterPolicyDetail', 'maturityAge')},
          {...getFilterForOpenLAPI('GPLUMasterPolicyDetail', 'doliMaturAge')},
        ]
      : [],
    attributes: [],
    dimensions,
  };
};

export const getMinMaxConfigRequestPayload = (
  payload: RetrievePlanConfigurationRequestPayload,
  currentCoverageCd: string
) => ({
  ...getRetrieveAttributeValuesPayload(payload, currentCoverageCd),
  _type: 'BehaviorRequest',
  behaviorTypes: ['AttributeMinMaxBehavior'],
  scoped: true,
});

const getEntityAndAttribute = (
  entityName: string,
  attributeName: string,
  attributeValueType: AttributeValue['valueType'],
  attributeValue: string | number | boolean
): {entityName: string; attributeName: string; attributeValue: any} => ({
  entityName,
  attributeName,
  attributeValue: {
    _type: 'Value',
    valueType: attributeValueType,
    [`${attributeValueType.toLowerCase()}Value`]: attributeValue,
  },
});

export const getCoverageTypeFromCode = (productCd: A360ProductCd, coverageCode: string) => {
  if (productCd === A360ProductCd.GPLU) {
    return GPLU_COVERAGE_CODE_TO_COVERAGE_TYPE_MAP[coverageCode];
  }
  return null;
};

export const getPredefinedHierarchicalContractsRequestPayload = (
  quoteData: ImmutableMap<string, any>
): PredefinedHierarchicalContractsRequestPayload => {
  return {
    premiumSequence: {
      policyInfo: {
        commissionStyle: quoteData?.get('commissionStyle') as string,
        effectiveDate: quoteData?.getIn(parsePath('termDetails.termEffectiveDate')) as string,
        enrollmentType: quoteData?.get('quotedEnrollmentType') as string,
        policyNumber: quoteData?.getIn(parsePath('policyDetails.policyNumber')) as string,
        producerSeries: quoteData?.get('quotedCommissionSeries') as string,
        productCode: quoteData?.get('_modelName') as string,
        stateCode: quoteData?.get('riskStateCd') as string,
        transactionEffectiveDate: quoteData?.getIn(parsePath('transactionDetails.txEffectiveDate')) as string,
      },
      premiumSplits: [
        {
          premiumSplitParticipants: [
            {
              producerLevel: quoteData?.get('quotedCommissionLevel') as number,
            },
          ],
        },
      ],
    },
  };
};

export const getRefreshProducerLevelListPayload = (quotedCommissionSeries?: string) => {
  const dimensions = [
    {id: '/', ...getEntityAndAttribute('GPLUMasterBenefitPolicyPackage', 'packageCd', 'String', 'WL')},
    {
    id: '/',
    ...getEntityAndAttribute('GPLUMasterPolicySummary', 'quotedCommissionSeries', 'String', quotedCommissionSeries),
  }
  ];

  return {
    dimensions,
    filters: [{...getFilterForOpenLAPI('GPLUMasterPolicySummary', 'quotedCommissionLevel')}],
    scoped: false,
    rateEffectiveDate: '2026-04-01T00:00:00.000Z',
    rateRequestDate: '2026-04-06T00:00:00.000Z',
  };
};

const getFilterForOpenLAPI = (entityName: string, attributeName: string) => ({
  _type: 'Value',
  entityName,
  attributeName,
});

export const getCalculateRatesPayload = (
  quote: ImmutableMap<string, any>,
  customerCensusClasses: any[],
  quoteRatesAndPremium: RatePremiumDetails | undefined,
  gpluAgeBandsAndSmokingFlags?: GpluAgeBandsAndSmokingFlags
) => {
  const productModel = quote.get('_modelName') as A360ProductModel;
  const quotePlans = quote.getIn(parsePath('benefitPackagingDetail.plans'));

  return {
    premiumSequences: [],
    _modelName: `${productModel}Rate`,
    _modelVersion: '1',
    rateAggregates: getRateAggregateForRateCalculation(
      productModel,
      quotePlans,
      quote,
      customerCensusClasses,
      gpluAgeBandsAndSmokingFlags
    ),
    _type: `${productModel}RateComposite`,
    _variation: 'quote',
    _key: quote.get('_key').toJS(),
    _modelType: 'GroupRate',
    _timestamp: isDefined(quoteRatesAndPremium) ? quoteRatesAndPremium?._timestamp : undefined,
    // _timestamp: getISODateString(DateTime.local())
  };
};

function getRateAggregateForRateCalculation(
  productModel: A360ProductModel,
  quotePlans: unknown,
  quote: ImmutableMap<string, any>,
  customerCensusClasses: any[],
  gpluAgeBandsAndSmokingFlags?: GpluAgeBandsAndSmokingFlags
) {
  const rateAggregate = {
    _type: `${productModel}RateAggregate`,
    _modelName: `${productModel}Rate`,
    _modelType: 'GroupRate',
    _modelVersion: '1',
    _variation: 'quote',
    rateEntries: [],
    ratePremiumEntry: getRatePremiumEntryForRateCalculation(
      productModel,
      quotePlans,
      quote,
      customerCensusClasses,
      gpluAgeBandsAndSmokingFlags
    ),
    rateTypeCd: 'Manual',
    packageCd: quote.getIn(parsePath('benefitPackagingDetail.packageCd')),
  };
  return [rateAggregate];
}

function getRatePremiumEntryForRateCalculation(
  productModel: A360ProductModel,
  quotePlans: unknown,
  quote: ImmutableMap<string, any>,
  customerCensusClasses: any[],
  gpluAgeBandsAndSmokingFlags?: GpluAgeBandsAndSmokingFlags
) {
  const productCd = getProductCdFromModel(productModel);

  return isA360HealthProduct(productModel)
    ? {
        _type: `${productCd}ExternalRatePremiumEntry`,
        policy: {
          _type: `${productCd}RatePremiumOnPolicy`,
          plans: (quotePlans as ImmutableMap<string, any>).map((plan: any) => ({
            _type: `${productCd}RatePremiumOnPlan`,
            coverages: plan.get('coverageDefs')?.map((covDef: any) => ({
              _type: `${productCd}RatePremiumOnCoverage`,
              classes: getPlanClassesForPremiumCalculation(
                productModel,
                covDef,
                quote.get('classGroups'),
                quote.getIn(parsePath('participationDetails.numberOfEligibleLives')) as number,
                customerCensusClasses
              ),
              coverageCode: covDef.get('code'),
            })),
            planName: plan.get('planName'),
          })),
        },
      }
    : {
        _type: `${productModel}RateExternalRatePremiumEntry`,
        policy: {
          _type: `${productModel}RateRatePremiumOnPolicy`,
          plans: (quotePlans as ImmutableMap<string, any>).map((plan: any) => ({
            _type: `${productModel}RateRatePremiumOnPlan`,
            coverages: plan.get('coverageDefs')?.map((covDef: any) => ({
              _type: `${productModel}RateRatePremiumOnCoverage`,
              classes: getPlanClassesForPremiumCalculation(
                productModel,
                covDef,
                quote.get('classGroups'),
                quote.getIn(parsePath('participationDetails.numberOfEligibleLives')) as number,
                customerCensusClasses,
                gpluAgeBandsAndSmokingFlags
              ),
              coverageType: covDef.get('code'),
            })),
            planName: plan.get('planName'),
          })),
        },
      };
}

const getPlanClassesForPremiumCalculation = (
  productModel: A360ProductModel,
  covDef: any,
  classGroups: any,
  numberOfEligibleLives: number,
  customerCensusClasses: any[],
  gpluAgeBandsAndSmokingFlags?: GpluAgeBandsAndSmokingFlags
) => {
  const result = covDef.get('coverageGroupsLinks')?.map((c: any) => {
    const match = classGroups.find(
      (cg: any) => cg.getIn(parsePath('_key.id')) === c.getIn(parsePath('groupLink._ref'))
    );
    return match ? match.getIn(parsePath('classGroupExtLink._uri')) : null;
  });

  const productCd = getProductCdFromModel(productModel);

  return isA360HealthProduct(productModel)
    ? customerCensusClasses
        .filter(c => result.includes(`geroot://CensusClassBase/CensusClass//${c?._key?.rootId}`))
        .map(cl => ({
          _type: `${productCd}RatePremiumOnClass`,
          className: cl.name,
          classNumber: String(cl.number),
          numberOfLives: numberOfEligibleLives,
          tiers: covDef.get('whoCovered')?.map((coveredTier: any) => ({
            _type: `${productCd}RatePremiumOnTier`,
            rateCard: {
              _type: `${productCd}RateCard`,
            },
            tier: coveredTier.get('tierCd'),
          })),
        }))
    : customerCensusClasses
        .filter(c => result.includes(`geroot://CensusClassBase/CensusClass//${c?._key?.rootId}`))
        .map(cl => ({
          _type: `${productModel}RateRatePremiumOnClass`,
          className: cl.name,
          classNumber: String(cl.number),
          rateCard:
            covDef?.get('code') === mandatoryCoverageByProduct[productModel]
              ? undefined
              : {
                  _type: `${productModel}RateRateCard`,
                },
          ageBandedRateCards:
            covDef?.get('code') === mandatoryCoverageByProduct[productModel]
              ? pipe(
                  gpluAgeBandsAndSmokingFlags.ageBands,
                  chain(ageBand =>
                    pipe(
                      gpluAgeBandsAndSmokingFlags.smokingFlags,
                      map(tobacco => ({
                        _type: `${productModel}RateAgeBandedRateCard`,
                        ageBand: ageBand,
                        rateCard: {
                          _type: `${productModel}RateRateCard`,
                        },
                        tobacco: tobacco,
                      }))
                    )
                  )
                )
              : [],
        }));
};

const mandatoryCoverageByProduct = {
  [A360ProductModel.GPLUMaster]: GPLUCoverageCode.WHOLE_LIFE,
} as const;
